import { useState } from "react";
import moment from "moment";
import { Icon } from "@iconify/react";

import {
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Radio,
  TextField,
  Tooltip,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";
import "./Steppers.css";

const Steppers = ({
  action,
  componentsValues,
  updateComponentsValues,
  templateDetail,
  handleSubmit,
  isSubmitting,
  isTemplateChanged,
  setIsTemplateChanged,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [headerError, setHeaderError] = useState("");
  const [bodyError, setBodyError] = useState("");
  const [anchorElAddButtonMenu, setAnchorElAddButtonMenu] = useState(null);
  const openAddButtonMenu = Boolean(anchorElAddButtonMenu);

  const validateSubmitButton = () => {
    if (currentStep === 1) {
      if (
        componentsValues.header.mediaType === "text" &&
        !componentsValues.header.headerText
      ) {
        return {
          submitDisabled: true,
          submitError: "Please fill the header content.",
        };
      }

      if (headerError) {
        return { submitDisabled: true };
      }

      if (checkVariableFormat() && !componentsValues.header.headerVariable) {
        return {
          submitDisabled: true,
          submitError: "Please fill the header variable.",
        };
      }
    } else if (currentStep === 2) {
      if (!componentsValues.bodyText) {
        return {
          submitDisabled: true,
          submitError: "Please fill the body content.",
        };
      }

      // Check if any body variable is empty
      let hasEmptyBodyVariable;

      if (templateDetail.parameterFormat === "positional") {
        hasEmptyBodyVariable = Array.from({ length: 10 }, (_, i) => i + 1).some(
          (num) => {
            const placeholder = `{{${num}}}`;
            const variable = componentsValues[`bodyVariable${num}`];
            return (
              componentsValues.bodyText?.includes(placeholder) && !variable
            );
          }
        );
      } else {
        // Find all {{...}} patterns in bodyText
        const matches = componentsValues.bodyText.match(/\{\{[^}]+\}\}/g) || [];

        // Check if any matched variable has an empty value
        hasEmptyBodyVariable = matches.some((placeholder, index) => {
          const variableValue = componentsValues[`bodyVariable${index + 1}`];
          return !variableValue; // placeholder exists but value is empty
        });
      }

      if (hasEmptyBodyVariable) {
        return {
          submitDisabled: true,
          submitError: "Please fill the body variables.",
        };
      }

      if (bodyError) {
        return { submitDisabled: true };
      }
    } else if (currentStep === 4) {
      // Checks for template status
      if (
        action === "edit" &&
        templateDetail.status !== "APPROVED" &&
        templateDetail.status !== "REJECTED" &&
        templateDetail.status !== "PAUSED"
      ) {
        return {
          submitDisabled: true,

          submitError:
            "You can only edit templates that are approved, rejected, or paused.",
        };
      }

      // Checks if this template has been edited in the last 24 hours
      if (
        action === "edit" &&
        moment().diff(moment(templateDetail.updatedTime), "hours") <= 24
      ) {
        return {
          submitDisabled: true,

          submitError:
            "You can only edit templates that have been edited more than 24 hours ago.",
        };
      }

      // Checks if there are any changes to the template
      if (action === "edit" && !isTemplateChanged) {
        return {
          submitDisabled: true,
          submitError: "You have not made any changes to the template.",
        };
      }

      // Checks for any empty template detail fields
      if (
        !templateDetail.name ||
        !templateDetail.language ||
        !templateDetail.category
      ) {
        return {
          submitDisabled: true,
          submitError: "Please fill in all the required fields.",
        };
      }

      // Checks for buttons invalidations
      if (
        componentsValues.button.some(
          (button) =>
            button.buttonPlaceholder === "" ||
            button.URLPhone === "" ||
            button.errorMessage ||
            button.isPlaceholderInvalid
        )
      ) {
        return {
          submitDisabled: true,

          submitError:
            "Please fill in all the required fields for each button.",
        };
      }

      // Disables button when submitting template
      if (isSubmitting) {
        return {
          submitDisabled: true,
          submitError: "Submitting... Please wait.",
        };
      }
    }

    return { submitDisabled: false, submitError: "" };
  };

  const { submitDisabled, submitError } = validateSubmitButton();

  const handleOpenAddButtonMenu = (e) => {
    setAnchorElAddButtonMenu(e.currentTarget);
  };

  const handleCloseAddButtonMenu = () => {
    setAnchorElAddButtonMenu(null);
  };

  // This function finds any {{n}} string and replaces it according to each variable value
  const renderBody = (body) => {
    const variableMap = Object.fromEntries(
      Array.from({ length: 10 }, (_, i) => {
        const key = `{{${i + 1}}}`;
        const value = componentsValues[`bodyVariable${i + 1}`] || key; // fallback to {{n}} if not defined
        return [key, value];
      })
    );

    let renderedBody = body.replace(
      /{{\d+}}/g,

      function (matched) {
        return variableMap[matched];
      }
    );

    return renderedBody;
  };

  const findMultipleVariables = (text) => {
    const matches = text.match(/\{{2}1\}/g); // This regex searches for {{1}} pattern
    return matches && matches.length;
  };

  const validateBodyText = (text) => {
    if (templateDetail.parameterFormat === "positional") {
      const allowed = Array.from({ length: 10 }, (_, i) => i + 1).join("|"); // "1|2|3|4|...|10"
      const limitRegex = new RegExp(`\\{{2}(?!(${allowed})\\})(\\d+)\\}`, "g");

      // This regex detects {number} pattern but ignores {{number}} pattern
      if (/(?<!\{){\d+}(?!\})/.test(text)) {
        setBodyError(
          "This template contains variable parameters with incorrect formatting. Variable parameters must be whole numbers with two sets of curly brackets (for example: {{1}}, {{2}}, {{3}})"
        );
      } else if (limitRegex.test(text)) {
        setBodyError("Your body content can't include more than ten variables");
      } else if (renderBody(text).length > 10000) {
        setBodyError(
          "Your body content should not be any longer than 10000 characters"
        );
      } else {
        setBodyError("");
      }
    } else {
      const matches = [...text.matchAll(/\{\{([^}]+)\}\}/g)];

      // This regex detects incorrect formatting
      if (
        /(?<!\{)\{[A-Za-z0-9]+\}(?!\})|\{\{(?:\d[A-Za-z0-9]*|[A-Z]+)\}\}/.test(
          text
        )
      ) {
        setBodyError(
          "This template contains variable parameters with incorrect formatting. Variable parameters must be lowercase characters, underscores and numbers with two sets of curly brackets (for example, {{customer_name}}, {{order_id}})"
        );
      } else if (matches.length > 10) {
        setBodyError("Your body content can't include more than ten variables");
      } else if (renderBody(text).length > 10000) {
        setBodyError(
          "Your body content should not be any longer than 10000 characters"
        );
      } else {
        setBodyError("");
      }
    }
  };

  const validateHeaderText = (text) => {
    if (templateDetail.parameterFormat === "positional") {
      // This regex detects {number} pattern but ignores {{number}} pattern
      if (/(?<!\{){\d+}(?!\})/.test(text)) {
        setHeaderError(
          "This template contains variable parameters with incorrect formatting. Variable parameters must be whole numbers with two sets of curly brackets (for example: {{1}})"
        );
      } else if (
        // This regex searches for any {{number}} pattern except {{1}}
        /\{{2}(?!1\})(\d+)\}/.test(text) ||
        findMultipleVariables(text) > 1
      ) {
        setHeaderError("Your title can't include more than one variable");
      } else if (
        text.replace(
          "{{1}}",
          componentsValues.header.headerVariable
            ? componentsValues.header.headerVariable
            : "{{1}}"
        ).length > 60
      ) {
        setHeaderError(
          "Your title should not be any longer than 60 characters"
        );
      } else {
        setHeaderError("");
      }
    } else {
      // This regex detects incorrect formatting
      if (
        /(?<!\{)\{[A-Za-z0-9]+\}(?!\})|\{\{(?:\d[A-Za-z0-9]*|[A-Z]+)\}\}/.test(
          text
        )
      ) {
        setHeaderError(
          "This template contains variable parameters with incorrect formatting. Variable parameters must be lowercase characters, underscores and numbers with two sets of curly brackets (for example, {{customer_name}}, {{order_id}})"
        );
      } else if (
        // This regex searches for multiple {{...}} pattern
        /(?:\{\{[^}]+\}\}).*(?:\{\{[^}]+\}\})/.test(text)
      ) {
        setHeaderError("Your title can't include more than one variable");
      } else if (text.length > 60) {
        setHeaderError(
          "Your title should not be any longer than 60 characters"
        );
      } else {
        setHeaderError("");
      }
    }
  };

  const validateButtonPlaceholder = (value, index) => {
    if (value !== "" && value.length > 25) {
      updateComponentsValues((draft) => {
        draft.button[index].isPlaceholderInvalid = true;
      });
    } else {
      updateComponentsValues((draft) => {
        draft.button[index].isPlaceholderInvalid = false;
      });
    }
  };

  // Validate button URL/phone number value
  const validateURLPhone = (type, value, index) => {
    if (type === "URL" && value !== "") {
      if (/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(value)) {
        updateComponentsValues((draft) => {
          draft.button[index].errorMessage = null;
        });
      } else {
        updateComponentsValues((draft) => {
          draft.button[index].errorMessage = "Please provide a valid URL";
        });
      }
    } else if (type === "PHONE_NUMBER" && value !== "") {
      if (/^\d{8,}$/.test(value)) {
        updateComponentsValues((draft) => {
          draft.button[index].errorMessage = null;
        });
      } else {
        updateComponentsValues((draft) => {
          draft.button[index].errorMessage =
            "Please provide a valid phone number";
        });
      }
    }
  };

  const countButtonType = (type) =>
    componentsValues.button.reduce(
      (count, obj) => (obj.buttonType === type ? count + 1 : count),
      0
    );

  const handleAddButton = (type) => {
    updateComponentsValues((draft) => {
      // find last button with type quick reply
      const findQuickReply = () =>
        draft.button.findLastIndex(
          (button) => button.buttonType === "QUICK_REPLY"
        );

      // Group quick reply buttons together
      if (findQuickReply && type === "QUICK_REPLY") {
        draft.button.splice(findQuickReply, 0, {
          buttonType: type,
          buttonPlaceholder: "Button Text",
        });
        // Else just add the button
      } else {
        draft.button.push({
          buttonType: type,
          buttonPlaceholder: "Button Text",
          errorMessage: null,
          isPlaceHolderInvalid: false,
          URLPhone: "",
        });
      }
    });

    handleCloseAddButtonMenu();
  };

  // Used to disable/enable add variable button in the body
  function countVariables() {
    if (templateDetail.parameterFormat === "positional") {
      // Disabled after 10 {{n}} is in the body
      return Array.from({ length: 10 }, (_, i) => `{{${i + 1}}}`).every(
        (text) => componentsValues.bodyText.includes(text)
      );
    } else {
      return (
        (componentsValues.bodyText.match(/\{\{[^}]+\}\}/g) || []).length >= 10
      );
    }
  }

  function renderBodyVariableTextFields() {
    let textFields = [];

    if (templateDetail.parameterFormat === "positional") {
      // Find all instances of "{{n}}" in the text
      const variables = [
        ...componentsValues.bodyText.matchAll(/\{\{(\d+)\}\}/g),
      ].map((match) => Number(match[1]));

      variables.forEach((num) => {
        const key = `bodyVariable${num}`;

        textFields.push(
          <TextField
            key={num}
            label={`Variable ${num}`}
            required
            value={componentsValues[key]}
            onChange={(e) =>
              updateComponentsValues((draft) => {
                draft[key] = e.target.value;
              })
            }
          />
        );
      });
    } else {
      // Find all instances of named variables in the text
      const variables = [
        ...componentsValues.bodyText.matchAll(/\{\{([^}]+)\}\}/g),
      ];

      variables.forEach((match, index) => {
        const key = `bodyVariable${index + 1}`;

        textFields.push(
          match[1].includes("date") || match[1].includes("tanggal") ? (
            <DatePicker
              format="DD/MM/YYYY"
              label={match[1]}
              value={
                componentsValues[key]
                  ? moment(componentsValues[key], "DD-MM-yyyy")
                  : null
              }
              renderInput={(params) => <TextField {...params} />}
              onChange={(e) =>
                updateComponentsValues((draft) => {
                  draft[key] = e ? moment(e).format("DD-MM-yyyy") : null;
                })
              }
            />
          ) : (
            <TextField
              key={index + 1}
              label={match[1]}
              required
              value={componentsValues[key]}
              onChange={(e) =>
                updateComponentsValues((draft) => {
                  draft[key] = e.target.value;
                })
              }
            />
          )
        );
      });
    }

    return textFields;
  }

  function checkVariableFormat() {
    return templateDetail.parameterFormat === "positional"
      ? // Positional variable
        componentsValues.header.headerText?.includes("{{1}}")
      : // Named variable
        /\{\{(?!\d)(?![A-Z]+)[^}]+\}\}/g.test(
          componentsValues.header.headerText ?? ""
        );
  }

  function renderHeaderVariableTextField() {
    if (
      componentsValues.header.headerVariableName &&
      (componentsValues.header.headerVariableName.includes("date") ||
        componentsValues.header.headerVariableName.includes("tanggal"))
    ) {
      return (
        <DatePicker
          format="DD/MM/YYYY"
          label={componentsValues.header.headerVariableName}
          value={
            componentsValues.header.headerVariable
              ? moment(componentsValues.header.headerVariable, "DD-MM-yyyy")
              : null
          }
          renderInput={(params) => <TextField {...params} />}
          onChange={(e) =>
            updateComponentsValues((draft) => {
              draft.header.headerVariable = e
                ? moment(e).format("DD-MM-yyyy")
                : null;
            })
          }
        />
      );
    } else {
      return (
        <TextField
          label={
            templateDetail.parameterFormat === "positional"
              ? "Variable"
              : componentsValues.header.headerVariableName
          }
          required
          value={componentsValues.header.headerVariable}
          onChange={(e) => {
            updateComponentsValues((draft) => {
              draft.header.headerVariable = e.target.value;
            });
          }}
        />
      );
    }
  }

  return (
    <section className="steppers">
      <div className="steps-container">
        <div className="steps">
          <div className="step">
            <Radio
              checked={currentStep === 1}
              // onChange={() => setCurrentStep(1)}
            />

            <p className="step-label">Header</p>
          </div>

          <span className="step-line" />

          <div className="step">
            <Radio
              checked={currentStep === 2}
              // onChange={() => setCurrentStep(2)}
            />

            <p className="step-label">Body</p>
          </div>

          <span className="step-line" />

          <div className="step">
            <Radio
              checked={currentStep === 3}
              // onChange={() => setCurrentStep(3)}
            />

            <p className="step-label">Footer</p>
          </div>

          <span className="step-line" />

          <div className="step">
            <Radio
              checked={currentStep === 4}
              // onChange={() => setCurrentStep(4)}
            />

            <p className="step-label">Button</p>
          </div>
        </div>
      </div>

      <section className="step-contents">
        <header>
          <p className="step-name">
            {(() => {
              switch (currentStep) {
                case 1:
                  return "Header";

                case 2:
                  return "Body";

                case 3:
                  return "Footer";

                case 4:
                  return "Button";

                default:
                  break;
              }
            })()}
          </p>

          <div className="requirement">
            {currentStep === 4 && (
              <>
                <button
                  className="add-button"
                  disabled={templateDetail.isCallPermission}
                  onClick={handleOpenAddButtonMenu}
                >
                  <Icon height="22" icon="ic:round-add" />
                  Add a Button
                  <Icon height="16" icon="icon-park-solid:down-one" />
                </button>

                <Menu
                  anchorEl={anchorElAddButtonMenu}
                  open={openAddButtonMenu}
                  onClose={handleCloseAddButtonMenu}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  {/* Limit to 3 quick reply buttons */}
                  <MenuItem
                    disabled={countButtonType("QUICK_REPLY") > 2}
                    onClick={() => handleAddButton("QUICK_REPLY")}
                  >
                    Quick Reply
                  </MenuItem>

                  {/* Limit to 2 visit website buttons */}
                  <MenuItem
                    disabled={countButtonType("URL") > 1}
                    onClick={() => handleAddButton("URL")}
                  >
                    Visit Website
                  </MenuItem>

                  {/* Limit to 1 call phone number button */}
                  <MenuItem
                    disabled={countButtonType("PHONE_NUMBER") > 0}
                    onClick={() => handleAddButton("PHONE_NUMBER")}
                  >
                    Call Phone Number
                  </MenuItem>
                </Menu>
              </>
            )}

            <p>{currentStep === 2 ? "Required" : "Optional"}</p>
          </div>
        </header>

        <div className="form">
          {(() => {
            switch (currentStep) {
              case 1:
                return (
                  <>
                    <TextField
                      label="Media Type"
                      select
                      value={componentsValues.header.mediaType}
                      onChange={(e) => {
                        setIsTemplateChanged(true);

                        updateComponentsValues((draft) => {
                          draft.header.attachment = {
                            name: null,
                            type: null,
                            size: null,
                            file: null,
                          };

                          draft.header.headerText = null;
                          draft.header.mediaType = e.target.value;
                        });

                        setHeaderError("");
                      }}
                    >
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="image">Image</MenuItem>
                      <MenuItem value="video">Video</MenuItem>
                      <MenuItem value="document">Document</MenuItem>
                      {/* <MenuItem value="location">Location</MenuItem> */}
                    </TextField>

                    {componentsValues.header.mediaType === "image" ||
                    componentsValues.header.mediaType === "video" ||
                    componentsValues.header.mediaType === "document" ? (
                      <TextField
                        className="input-attachment"
                        label="Attachments"
                        type="file"
                        inputProps={{
                          accept:
                            componentsValues.header.mediaType === "image"
                              ? ".jpeg,.jpg,.png,image/jpeg,image/png"
                              : componentsValues.header.mediaType === "video"
                              ? ".mp4,video/mp4"
                              : ".pdf,application/pdf",
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {componentsValues.header.attachment && (
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title="Clear attachment"
                                >
                                  <IconButton
                                    onClick={() => {
                                      updateComponentsValues((draft) => {
                                        draft.header.attachment = {
                                          name: null,
                                          type: null,
                                          size: null,
                                          file: null,
                                        };
                                      });
                                    }}
                                  >
                                    <Icon height="26" icon="carbon:close" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <Icon icon="mdi:attachment-vertical" width="26" />
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                          setIsTemplateChanged(true);

                          try {
                            let file = e.target.files[0];
                            let reader = new FileReader();
                            reader.readAsDataURL(file);

                            reader.onload = () => {
                              updateComponentsValues((draft) => {
                                draft.header.attachment.file = reader.result;
                              });
                            };

                            updateComponentsValues((draft) => {
                              // Get file type/extension
                              draft.header.attachment.type = file.type;
                              draft.header.attachment.size = file.size;
                              draft.header.attachment.name = file.name;
                            });
                          } catch (error) {
                            console.log("error upload: ", error);
                          }
                        }}
                      />
                    ) : componentsValues.header.mediaType === "text" ? (
                      <>
                        <TextField
                          error={headerError}
                          label="Header Text"
                          required
                          value={componentsValues.header.headerText}
                          onChange={(e) => {
                            setIsTemplateChanged(true);

                            updateComponentsValues((draft) => {
                              draft.header.headerText = e.target.value;

                              // Extract variable name if using named format
                              if (templateDetail.parameterFormat === "named") {
                                const matchedPattern =
                                  e.target.value.match(/\{\{([^}]+)\}\}/);

                                if (matchedPattern) {
                                  draft.header.headerVariableName =
                                    matchedPattern[1];
                                }
                              }
                            });

                            validateHeaderText(e.target.value);
                          }}
                        />

                        {headerError && (
                          <p className="validation-error">{headerError}</p>
                        )}

                        <div className="add-variable">
                          <hr />

                          <Button
                            className="add-variable-button"
                            disabled={checkVariableFormat()}
                            startIcon={
                              <Icon icon="material-symbols:add-rounded" />
                            }
                            variant="outlined"
                            onClick={() =>
                              updateComponentsValues((draft) => {
                                if (
                                  templateDetail.parameterFormat ===
                                  "positional"
                                ) {
                                  draft.header.headerText =
                                    draft.header.headerText + " {{1}}";
                                } else {
                                  draft.header.headerText =
                                    draft.header.headerText +
                                    " {{variable_name}}";

                                  draft.header.headerVariableName =
                                    "variable_name";
                                }
                              })
                            }
                          >
                            Add Variable
                          </Button>
                        </div>

                        {checkVariableFormat() &&
                          renderHeaderVariableTextField()}
                      </>
                    ) : (
                      ""
                    )}
                  </>
                );

              case 2:
                return (
                  <>
                    <TextField
                      label="Body Text"
                      multiline
                      required
                      value={componentsValues.bodyText}
                      onChange={(e) => {
                        setIsTemplateChanged(true);

                        updateComponentsValues((draft) => {
                          draft.bodyText = e.target.value;

                          // Extract variable name if using named format
                          if (templateDetail.parameterFormat === "named") {
                            const matches = [
                              ...e.target.value.matchAll(/\{\{([^}]+)\}\}/g),
                            ];

                            matches.forEach((match, index) => {
                              const key = `bodyVariableName${index + 1}`;
                              draft[key] = match[1]; // the text inside {{...}}
                            });
                          }
                        });

                        validateBodyText(e.target.value);
                      }}
                    />

                    {bodyError && (
                      <p className="validation-error">{bodyError}</p>
                    )}

                    <div className="add-variable">
                      <hr />

                      <Button
                        className="add-variable-button"
                        disabled={countVariables()}
                        startIcon={<Icon icon="material-symbols:add-rounded" />}
                        variant="outlined"
                        onClick={() => {
                          setIsTemplateChanged(true);

                          updateComponentsValues((draft) => {
                            if (
                              templateDetail.parameterFormat === "positional"
                            ) {
                              // Find the first missing placeholder from {{1}} to {{10}}
                              const nextNum = Array.from(
                                { length: 10 },
                                (_, i) => i + 1
                              ).find(
                                (num) => !draft.bodyText.includes(`{{${num}}}`)
                              );

                              // If there’s still an available variable slot, append it
                              if (nextNum) {
                                const newPlaceholder = `{{${nextNum}}}`;

                                draft.bodyText =
                                  draft.bodyText + ` ${newPlaceholder}`;

                                draft[`bodyVariable${nextNum}`] =
                                  newPlaceholder;
                              }
                            } else {
                              // Find all existing {{...}} patterns
                              const matches =
                                draft.bodyText.match(/\{\{[^}]+\}\}/g) || [];

                              // Determine next variable index
                              const nextIndex = matches.length + 1; // 1..10

                              // Add placeholder to text
                              draft.bodyText =
                                draft.bodyText + " {{variable_name}}";

                              // Save to proper variable slot: bodyVariable1–bodyVariable10
                              draft[`bodyVariable${nextIndex}`] =
                                "{{variable_name}}";
                            }
                          });
                        }}
                      >
                        Add Variable
                      </Button>
                    </div>

                    {renderBodyVariableTextFields()}
                  </>
                );

              case 3:
                return (
                  <TextField
                    label="Footer Text"
                    value={componentsValues.footerText}
                    onChange={(e) => {
                      setIsTemplateChanged(true);

                      updateComponentsValues((draft) => {
                        draft.footerText = e.target.value;
                      });
                    }}
                  />
                );

              case 4: {
                return templateDetail.isCallPermission ? (
                  <p className="not-allowed">
                    Buttons are not allowed in call request templates.
                  </p>
                ) : (
                  componentsValues.button.map((button, index) => (
                    <section className="button-group" key={index}>
                      <div className="button-contents">
                        <header>
                          <p className="button-number">Button{index + 1}</p>
                        </header>

                        <TextField
                          label="Button Type"
                          select
                          value={button.buttonType}
                          onChange={(e) => {
                            setIsTemplateChanged(true);

                            updateComponentsValues((draft) => {
                              draft.button[index].buttonType = e.target.value;
                            });
                          }}
                        >
                          {
                            // Limit to 3 quick reply buttons
                            <MenuItem
                              disabled={countButtonType("QUICK_REPLY") > 2}
                              value="QUICK_REPLY"
                            >
                              Quick Reply
                            </MenuItem>
                          }

                          {
                            // Limit to 2 visit website buttons
                            <MenuItem
                              disabled={countButtonType("URL") > 1}
                              value="URL"
                            >
                              Visit Website
                            </MenuItem>
                          }

                          {
                            // Limit to 1 call phone number button
                            <MenuItem
                              disabled={countButtonType("PHONE_NUMBER") > 0}
                              value="PHONE_NUMBER"
                            >
                              Call Phone Number
                            </MenuItem>
                          }
                        </TextField>

                        <TextField
                          error={button.isPlaceholderInvalid}
                          label="Button Placeholder"
                          required
                          value={button.buttonPlaceholder}
                          onChange={(e) => {
                            setIsTemplateChanged(true);
                            validateButtonPlaceholder(e.target.value, index);

                            updateComponentsValues((draft) => {
                              draft.button[index].buttonPlaceholder =
                                e.target.value;
                            });
                          }}
                        />

                        {button.isPlaceholderInvalid && (
                          <p className="validation-error">
                            Button text should not be any longer than 25
                            characters
                          </p>
                        )}

                        {(button.buttonType === "URL" ||
                          button.buttonType === "PHONE_NUMBER") && (
                          <>
                            <TextField
                              error={button.errorMessage}
                              label={
                                button.buttonType === "URL"
                                  ? "Website URL"
                                  : "Phone Number"
                              }
                              required
                              value={button.URLPhone}
                              onChange={(e) => {
                                setIsTemplateChanged(true);

                                validateURLPhone(
                                  button.buttonType,
                                  e.target.value,
                                  index
                                );

                                updateComponentsValues((draft) => {
                                  draft.button[index].URLPhone = e.target.value;
                                });
                              }}
                            />

                            {button.errorMessage && (
                              <p className="validation-error">
                                {button.errorMessage}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      <Tooltip arrow placement="top" title="Remove button">
                        <IconButton
                          onClick={() => {
                            setIsTemplateChanged(true);

                            updateComponentsValues((draft) => {
                              draft.button.splice(index, 1);
                            });
                          }}
                        >
                          <Icon
                            color="#727272"
                            height="24"
                            icon="iconamoon:close"
                          />
                        </IconButton>
                      </Tooltip>
                    </section>
                  ))
                );
              }

              default:
                break;
            }
          })()}
        </div>

        <div className="prev-next-button">
          {currentStep !== 1 && (
            <button
              className="previous"
              onClick={() => setCurrentStep((prevState) => prevState - 1)}
            >
              Prev
            </button>
          )}

          <button
            className="proceed"
            disabled={submitDisabled}
            onClick={() => {
              if (currentStep === 4) {
                handleSubmit();
              } else {
                setCurrentStep((prevState) => prevState + 1);
              }
            }}
          >
            {currentStep === 4 && isSubmitting && (
              <Icon height="22" icon="eos-icons:loading" />
            )}

            {isSubmitting
              ? "Submitting..."
              : currentStep === 4
              ? "Submit Template"
              : "Next"}
          </button>
        </div>

        {submitError && (
          <p className="validation-error submit-error">{submitError}</p>
        )}
      </section>
    </section>
  );
};

export default Steppers;
